/**
 * SignUp.jsx - User Registration Page
 * 
 * @description
 * Registration page for new users to create a Link Spaghetti account.
 * Includes comprehensive form validation and password strength indicator.
 * 
 * @route /signup
 * @auth Not required (public page)
 * @redirects /dashboard on successful registration
 * 
 * @formFields
 * - name: Full name (required)
 * - email: Email address (required, validated)
 * - password: Password (required, min 8 chars, strength checked)
 * - confirmPassword: Password confirmation (must match)
 * - agreeToTerms: Terms acceptance checkbox (required)
 * 
 * @features
 * - Comprehensive form validation
 * - Password strength indicator
 * - Real-time validation feedback
 * - Loading states
 * - Toast notifications
 * - Dark theme support
 * - Mobile responsive
 * 
 * @passwordStrength
 * Calculates strength based on:
 * - Length (8+ characters)
 * - Uppercase letters
 * - Lowercase letters
 * - Numbers
 * - Special characters
 * 
 * Levels: Very Weak, Weak, Fair, Good, Strong
 * 
 * @validation
 * - Name: Required, min 2 characters
 * - Email: Required, valid format
 * - Password: Required, min 8 characters
 * - Confirm Password: Must match password
 * - Terms: Must be accepted
 * 
 * @registration
 * - Creates account via backend API
 * - Handles validation and error responses
 * - Auto-login after successful registration
 * 
 * @stateManagement
 * - formData: All form field values
 * - errors: Field-specific error messages
 * - isLoading: Submission state
 * - showPassword/showConfirmPassword: Visibility toggles
 * 
 * @styling
 * - Gradient background design
 * - Dark mode with themed colors
 * - Animated elements
 * - Color-coded password strength
 * 
 * @dependencies
 * - LoadingButton for submit state
 * - Toast for notifications
 * - React Router for navigation
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LoadingButton } from '../components/LoadingSpinner';
import { useToast } from '../components/Toast';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { register, getApiErrorMessage } from '../services/api.js';
import { sendVerificationEmail } from '../services/emailService.js';
import { analyticsService } from '../services/analytics.js';

const SignUp = () => {
  const navigate = useNavigate();
  const [theme] = useState(() => localStorage.getItem('theme') || 'light');
  const { showSuccess, showError, ToastContainer } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Apply theme on mount
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Calculate password strength
  useEffect(() => {
    const password = formData.password;
    let strength = 0;
    
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;
    
    setPasswordStrength(strength);
  }, [formData.password]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Please fix the errors in the form');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Call backend register API
      const res = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        username: formData.email.split('@')[0], // or add username field to form
      });
      
      // Send verification email after successful registration
      try {
        await sendVerificationEmail(formData.email);
        console.log('Verification email sent successfully');
      } catch (emailError) {
        console.warn('Failed to send verification email:', emailError);
        // Don't fail registration if email service is down
      }
      
      // The API service already stores the JWT, no need to store it again here
      
      // Track successful sign-up for analytics with enhanced tracking
      analyticsService.trackSignup({
        id: res.user?.id || res.id,
        method: 'email',
        tier: res.user?.tier || 'free'
      });

      // Also track with the generic event endpoint
      try {
        await analyticsService.trackEvent('sign_up', {
          signup_method: 'email',
          user_tier: res.user?.tier || 'free',
          utm_source: new URLSearchParams(window.location.search).get('utm_source'),
          utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
          utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
          referral_code: new URLSearchParams(window.location.search).get('ref')
        });
      } catch (trackingError) {
        console.warn('Failed to track signup with generic endpoint:', trackingError);
      }
      
      showSuccess('Account created successfully! Please check your email to verify your account.');
      
      // Longer delay to ensure token is properly stored and processed
      // Keep loading state active during delay
      setTimeout(() => {
        navigate('/dashboard');
        setIsLoading(false);
      }, 1000);
    } catch (error) {
      showError(getApiErrorMessage(error));
      setIsLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500'];
    return colors[passwordStrength] || 'bg-gray-300';
  };

  const getPasswordStrengthText = () => {
    const texts = ['Weak', 'Fair', 'Good', 'Strong'];
    return texts[passwordStrength - 1] || '';
  };

  return (
    <div className={`min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 ${theme === 'dark' ? 'themed' : 'bg-gradient-to-br from-purple-50 to-pink-50'}`}>
      <ToastContainer />
      
      <div className="max-w-md w-full space-y-8 animate-fade-in-up">
        <div className="text-center">
          <Link to="/" className="flex items-center justify-center">
            <img src="/logo.svg" alt="Link Spaghetti Logo" className="h-10 mb-2" />
          </Link>
          <Link to="/" className={`text-3xl font-bold inline-block ${theme === 'dark' ? 'text-transparent bg-gradient-to-r from-[#2980b9] to-[#16a085] bg-clip-text' : 'bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent'}`}>
            Link Spaghetti
          </Link>
          <h2 className={`mt-6 text-3xl font-extrabold ${theme === 'dark' ? 'themed-text' : 'text-gray-900'}`}>
            Create your account
          </h2>
          <p className={`mt-2 text-sm ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'}`}>
            Already have an account?{' '}
            <Link to="/signin" className={`font-medium ${theme === 'dark' ? 'text-[#2980b9] hover:text-[#16a085]' : 'text-purple-600 hover:text-purple-500'}`}>
              Sign in
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className={`${theme === 'dark' ? 'themed-secondary' : 'bg-white'} py-8 px-4 shadow-lg rounded-lg sm:px-10`}>
            <div className="space-y-6">
              {/* Name Field */}
              <div className="animate-fade-in" style={{ animationDelay: '100ms' }}>
                <label htmlFor="name" className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'}`}>
                  Full Name
                </label>
                <div className="mt-1">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className={`appearance-none block w-full px-3 py-2 border ${
                      theme === 'dark' ? 'themed-tertiary themed-border border-gray-600 text-white placeholder-gray-400 focus:border-[#2980b9]' : 'border-gray-300 placeholder-gray-400 focus:border-purple-500'
                    } rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                      theme === 'dark' ? 'focus:ring-[#2980b9]' : 'focus:ring-purple-500'
                    } sm:text-sm ${errors.name ? 'border-red-500 animate-shake' : ''}`}
                    placeholder="John Doe"
                    disabled={isLoading}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-500 animate-fade-in">{errors.name}</p>
                  )}
                </div>
              </div>

              {/* Email Field */}
              <div className="animate-fade-in" style={{ animationDelay: '200ms' }}>
                <label htmlFor="email" className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'}`}>
                  Email Address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className={`appearance-none block w-full px-3 py-2 border ${
                      theme === 'dark' ? 'themed-tertiary themed-border border-gray-600 text-white placeholder-gray-400 focus:border-[#2980b9]' : 'border-gray-300 placeholder-gray-400 focus:border-purple-500'
                    } rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                      theme === 'dark' ? 'focus:ring-[#2980b9]' : 'focus:ring-purple-500'
                    } sm:text-sm ${errors.email ? 'border-red-500 animate-shake' : ''}`}
                    placeholder="you@example.com"
                    disabled={isLoading}
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-500 animate-fade-in">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* Password Field */}
              <div className="animate-fade-in" style={{ animationDelay: '300ms' }}>
                <label htmlFor="password" className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'}`}>
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className={`appearance-none block w-full px-3 py-2 border ${
                      theme === 'dark' ? 'themed-tertiary themed-border border-gray-600 text-white placeholder-gray-400 focus:border-[#2980b9]' : 'border-gray-300 placeholder-gray-400 focus:border-purple-500'
                    } rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                      theme === 'dark' ? 'focus:ring-[#2980b9]' : 'focus:ring-purple-500'
                    } sm:text-sm ${errors.password ? 'border-red-500 animate-shake' : ''}`}
                    placeholder="••••••••"
                    disabled={isLoading}
                  />
                  {errors.password && (
                    <p className="mt-1 text-xs text-red-500 animate-fade-in">{errors.password}</p>
                  )}
                  
                  {/* Password Strength Indicator */}
                  {formData.password && (
                    <div className="mt-2 animate-fade-in">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                            style={{ width: `${(passwordStrength / 4) * 100}%` }}
                          />
                        </div>
                        <span className={`text-xs ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-600'}`}>
                          {getPasswordStrengthText()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Confirm Password Field */}
              <div className="animate-fade-in" style={{ animationDelay: '400ms' }}>
                <label htmlFor="confirmPassword" className={`block text-sm font-medium ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-700'}`}>
                  Confirm Password
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`appearance-none block w-full px-3 py-2 border ${
                      theme === 'dark' ? 'themed-tertiary themed-border border-gray-600 text-white placeholder-gray-400 focus:border-[#2980b9]' : 'border-gray-300 placeholder-gray-400 focus:border-purple-500'
                    } rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                      theme === 'dark' ? 'focus:ring-[#2980b9]' : 'focus:ring-purple-500'
                    } sm:text-sm ${errors.confirmPassword ? 'border-red-500 animate-shake' : ''}`}
                    placeholder="••••••••"
                    disabled={isLoading}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-xs text-red-500 animate-fade-in">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>

              {/* Terms Checkbox */}
              <div className="animate-fade-in" style={{ animationDelay: '500ms' }}>
                <div className="flex items-start">
                  <input
                    id="acceptTerms"
                    name="acceptTerms"
                    type="checkbox"
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    className={`h-4 w-4 ${
                      theme === 'dark' ? 'text-[#2980b9] focus:ring-[#2980b9] border-gray-600 bg-gray-700' : 'text-purple-600 focus:ring-purple-500 border-gray-300'
                    } rounded ${errors.acceptTerms ? 'border-red-500' : ''}`}
                    disabled={isLoading}
                  />
                  <label htmlFor="acceptTerms" className={`ml-2 block text-sm ${theme === 'dark' ? 'themed-text-secondary' : 'text-gray-900'}`}>
                    I agree to the{' '}
                    <Link to="/terms" className={`font-medium ${theme === 'dark' ? 'text-[#2980b9] hover:text-[#16a085]' : 'text-purple-600 hover:text-purple-500'}`}>
                      Terms and Conditions
                    </Link>
                    {' '}and{' '}
                    <Link to="/privacy" className={`font-medium ${theme === 'dark' ? 'text-[#2980b9] hover:text-[#16a085]' : 'text-purple-600 hover:text-purple-500'}`}>
                      Privacy Policy
                    </Link>
                  </label>
                </div>
                {errors.acceptTerms && (
                  <p className="mt-1 text-xs text-red-500 animate-fade-in">{errors.acceptTerms}</p>
                )}
              </div>

              <div className="animate-fade-in" style={{ animationDelay: '600ms' }}>
                <LoadingButton
                  type="submit"
                  loading={isLoading}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    theme === 'dark' ? 'bg-gradient-to-r from-[#2980b9] to-[#16a085] hover:from-[#3498db] hover:to-[#1abc9c]' : 'button-gradient'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    theme === 'dark' ? 'focus:ring-[#2980b9]' : 'focus:ring-purple-500'
                  } transition-all duration-200 transform hover:scale-105`}
                  disabled={isLoading || Object.keys(errors).length > 0}
                >
                  Create Account
                </LoadingButton>
              </div>

              <div className="mt-6 animate-fade-in" style={{ animationDelay: '700ms' }}>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className={`w-full border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`} />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className={`px-2 ${theme === 'dark' ? 'themed-secondary themed-text-secondary' : 'bg-gray-50 text-gray-500'}`}>
                      Or continue with
                    </span>
                  </div>
                </div>

                <div className="mt-6">
                  <GoogleSignInButton 
                    theme={theme}
                    disabled={isLoading}
                    className="animate-fade-in"
                    style={{ animationDelay: '800ms' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="text-center text-xs text-gray-500 animate-fade-in" style={{ animationDelay: '900ms' }}>
          By signing up, you agree to our{' '}
          <Link to="/privacy" className="underline hover:text-gray-700">Privacy Policy</Link>
          {' '}and{' '}
          <Link to="/terms" className="underline hover:text-gray-700">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
};

export default SignUp;